import {APIService} from "./APIService";
import {
    GetFilterCriteriaResponse,
    GetFilterResponse,
    GetHomeFilterSearchResultByCityUniqueResponse,
    GetNearbyFilterSearchResultByPointResponse,
    NearbySearchLocation
} from "../types/FilterTypes";
import { CancelTokenSource } from "axios";
import {PricingDTO} from "../../module/Deals/view/vue/domain/dto/PricingDTO";

export type DealTravelDistance = {
    origin: null,
    destination: null,
    distance: number,
    duration: string,
    label: null,
    icon: string
}

export interface INearbyFilterResponse {
    unique: string,
    campaign_unique: string,
    marker_key: string,
    is_new_today: boolean,
    is_for_sale: boolean,
    discount: number,
    image: string,
    title: string,
    company_name: string,
    location: string,
    sold: string,
    pricing: PricingDTO,
    start_date: string,
    category_id: number,

    sub_category_id: number,
    category_key: string,
    category_color: string,
    latitude: number,
    longitude: number,
    travel_distance: DealTravelDistance,
    review_stats: {
        average: number,
        label: string,
        amount_of_reviews: null,
        stars: number,
        show_total: number,
        total: number,
        total_text: string,
        star1: number,
        star2: number,
        star3: number,
        star4: number,
        star5: number
    },
    city_slug: string,
    company_slug: string,
    deal_slug: string,
    template_urls: {
        absolute: string,
        relative: string
    },
    is_favorite: false,
    is_favorite_button_visible: true,
    pills: [],
    tags: [
        {
            name: string
        }
    ],
    _links: {
        self: {
            href: string
        },
        deal_link: {
            href: string
        }
    }
}

export interface INearbyFilterResponse {
    deals: Array<Array<INearbyFilterResponse>>;
}

export default class FilterService extends APIService {
    public static getHomeFilterCriteriaByCityUnique( cityUnique: string, resetFilters: boolean = false, filterQueryString: string = ''): Promise<GetFilterCriteriaResponse> {
        let resetQueryParam = resetFilters ? 'reset=true' : '';
        return this.get<GetFilterCriteriaResponse>( `/proxy/deal-filter/home/${cityUnique}/filter-criteria/?${filterQueryString}${resetQueryParam}`, {
            headers : this.getHeaders(
                {
                    'Accept': 'application/vnd.deal-filter-api.v2+json'
                }
            ),
        } );
    }

    public static getHomeFilterByCityUnique( cityUnique: string, filterQueryString: string, cancelTokenSource?: CancelTokenSource): Promise<GetFilterResponse> {
        return this.get<GetFilterResponse>( `/proxy/deal-filter/home/${cityUnique}/filters/?${filterQueryString}`, {
            headers : this.getHeaders(
                {
                    'Accept': 'application/vnd.deal-filter-api.v2+json'
                }
            ),
            cancelToken: cancelTokenSource?.token
        } );
    }

    public static getHomeFilterSearchResultByCityUnique( cityUnique: string, filterQueryString: string): Promise<GetHomeFilterSearchResultByCityUniqueResponse> {
        return this.get<any>( `/proxy/deal-filter/home/${cityUnique}/search-result/?${filterQueryString}`, {
            headers : this.getHeaders(
                {
                    'Accept': 'application/vnd.deal-filter-api.v2+json'
                }
            ),
        } );
    }

    public static getNearbyFilterCriteriaByPoint( point: string, resetFilters: boolean = false, filterQueryString: string = ''): Promise<GetFilterCriteriaResponse> {
        let resetQueryParam = resetFilters ? 'reset=true' : '';
        return this.get<GetFilterCriteriaResponse>( `/proxy/deal-filter/nearby/filter-criteria/?point=${point}&${filterQueryString}${resetQueryParam}`, {
            headers : this.getHeaders(
                {
                    'Accept': 'application/vnd.deal-filter-api.v2+json'
                }
            ),
        } );
    }

    public static getNearbyFilterByPoint( point: string, filterQueryString: string, cancelTokenSource?: CancelTokenSource): Promise<GetFilterResponse> {
        return this.get<GetFilterResponse>( `/proxy/deal-filter/nearby/filters/?point=${point}&${filterQueryString}`, {
            headers : this.getHeaders(
                {
                    'Accept': 'application/vnd.deal-filter-api.v2+json'
                }
            ),
            cancelToken: cancelTokenSource?.token
        } );
    }

    public static getNearbyFilterSearchResultByPoint( point: string, filterQueryString: string, pageNumber:number): Promise<GetNearbyFilterSearchResultByPointResponse> {
        return this.get<any>( `/proxy/deal-filter/nearby/search-result/?point=${point}&${filterQueryString}&page=${pageNumber}`, {
            headers : this.getHeaders(
                {
                    'Accept': 'application/vnd.deal-filter-api.v2+json'
                }
            ),
        } );
    }
    /**
     * Converts a filter object to a query parameter string
     * @param filters - The filter object to convert
     * @param flow - Optional flow parameter to append
     * @returns Query parameter string
     */
    public static getFilterQueryParam(filters: any, flow?: string): string {
        let filterQueryParam = '';
        for (const [key, value] of Object.entries(filters)) {
            if (typeof value === 'object') {
                for (const [subkey, subvalue] of Object.entries(value)) {
                    if (!subvalue) {
                        continue;
                    }
                    const exceedsMaxCap = String((value as any).exceeds_max_cap) === 'true';
                    if (key === 'price_range' && (subkey === 'exceeds_max_cap' || (subkey === 'max' && exceedsMaxCap))) {
                        continue;
                    }
                    filterQueryParam += `filter[${key}][${subkey}]=${encodeURIComponent(subvalue as string)}&`;
                }
            } else {
                filterQueryParam += `filter[${key}]=${encodeURIComponent(value as string | number)}&`;
            }
        }
        if (flow) {
            filterQueryParam += `flow=${flow}&`;
        }
        return filterQueryParam;
    }

    /**
     * Parses URL parameters into a filter object
     * @param isNearby - Whether this is for nearby filters (affects distance handling)
     * @returns Object containing filterObject and nearbyLocation (if applicable)
     */
    public static parseUrlFilters(isNearby: boolean = false): { filterObject: any; nearbyLocation: any } {
        const acceptedFilters = [
            'persisted',
            'sorting',
            'categories',
            'price_range[min]',
            'price_range[max]',
            'price_range[exceeds_max_cap]',
            'distance',
            'distance[location]',
            'distance[value]',
            'distance[lat]',
            'distance[lng]',
            'rating',
        ];

        let filterObject = {} as any;
        let nearbyLocation = {} as any;

        if (!isNearby) {
            filterObject.distance = {};
        }

        let url = decodeURIComponent(window.location.search.substring(1));
        if (url !== '') {
            let urlParts = url.split('&');
            for (let u = 0; u < urlParts.length; u++) {
                let param = urlParts[u].split('=');
                if (param[0] === 'category') {
                    param[0] = 'categories';
                }
                if (param[1] === 'favorites') {
                    param[1] = 'popular';
                }
                if (acceptedFilters.includes(param[0])) {
                    switch (param[0]) {
                        case 'price_range[min]':
                            if (!filterObject['price_range']) {
                                filterObject['price_range'] = {};
                            }
                            filterObject['price_range']['min'] = param[1];
                            break;
                        case 'price_range[max]':
                            if (!filterObject['price_range']) {
                                filterObject['price_range'] = {};
                            }
                            filterObject['price_range']['max'] = param[1];
                            break;
                        case 'price_range[exceeds_max_cap]':
                            if (!filterObject['price_range']) {
                                filterObject['price_range'] = {};
                            }
                            filterObject['price_range']['exceeds_max_cap'] = param[1];
                            break;
                        case 'distance[lat]':
                            if (isNearby) {
                                nearbyLocation.lat = param[1];
                            } else {
                                filterObject['distance']['lat'] = param[1];
                            }
                            break;
                        case 'distance[lng]':
                            if (isNearby) {
                                nearbyLocation.long = param[1];
                            } else {
                                filterObject['distance']['lng'] = param[1];
                            }
                            break;
                        case 'distance[value]':
                            if (isNearby) {
                                filterObject['distance'] = param[1];
                            } else {
                                filterObject['distance']['value'] = param[1];
                            }
                            break;
                        case 'distance[location]':
                            if (isNearby) {
                                filterObject['location'] = param[1];
                            } else {
                                filterObject['distance']['location'] = param[1];
                            }
                            break;
                        default:
                            filterObject[param[0]] = param[1];
                            break;
                    }
                }
            }
        }

        if (
            filterObject['distance'] &&
            typeof filterObject['distance'] !== 'number' &&
            Object.keys(filterObject['distance']).length === 0
        ) {
            delete filterObject['distance'];
        }

        return { filterObject, nearbyLocation };
    }

    public static removeDefaultValuesFromURL(filterQueryParameters: string):string {
        const filterQueryParams = filterQueryParameters.split( '&' );
        let queryParams = [] as any;
        for (let q = 0, qArrLength = filterQueryParams.length; q < qArrLength; q++) {
            let qArr = filterQueryParams[q].split('=');
            if( qArr[0] === '' || qArr[0] === 'flow'  || qArr[1] === 'null' || qArr[1] === '0' || qArr[1] === 'default' || qArr[1] === 'all' ) {
                continue;
            }

            let qKeyName = qArr[0].replace('filter[', '');
            let charPosition = qKeyName.indexOf( ']');
            let qPart1 = qKeyName.substring(0, charPosition);
            let qPart2 = qKeyName.substring(charPosition + 1, qKeyName.length);
            qKeyName = qPart1 + qPart2;

            queryParams[ qKeyName] = qArr[1];
        }
        let url = Object.entries(queryParams)
            .map(a => a.join('='))
            .join('&');
        return url;
    }

    /**
     * Navigates to the nearby page with current filters
     * @param filterQueryString - The filter query string to include in the URL
     * @param distanceLocation - Optional location name for the URL slug
     */
    public static navigateToNearby(filterQueryString: string, distanceLocation?: string): void {
        let nearby;
        switch ((window as any).lang['currentLocale']) {
            case 'de':
                nearby = 'in-der-nahe';
                break;
            case 'fr':
                nearby = 'a-proximite';
                break;
            case 'en':
                nearby = 'nearby';
                break;
            case 'be':
            case 'nl':
            default:
                nearby = 'dichtbij';
                break;
        }

        const url = this.removeDefaultValuesFromURL(filterQueryString);
        const slugifiedCity = distanceLocation ? (window as any).slugify(distanceLocation) + '/' : '';

        window.location.assign(`/${nearby}/${slugifiedCity}?${url}`);
    }

    /**
     * Parses base64-encoded nearby search bar data
     * @param encodedData - Base64 encoded search bar data
     * @returns Parsed NearbySearchLocation or undefined if no data
     */
    public static parseNearbySearchBar(encodedData: string): NearbySearchLocation | undefined {
        return encodedData ? JSON.parse(atob(encodedData)) : undefined;
    }

    /**
     * Formats a location object as a point string (lat,long)
     * @param location - The nearby search location
     * @returns Point string in format "lat,long"
     */
    public static formatLocationAsPoint(location: NearbySearchLocation): string {
        return `${location.lat},${location.long}`;
    }

    /**
     * Formats filter response for display in the UI
     * @param filters - The filter response from the API
     * @param isNearby - Whether this is a nearby filter
     * @param cityToRestore - Optional city data to restore
     * @returns Formatted filter response
     */
    public static formatFilters(
        filters: GetFilterResponse,
        isNearby: boolean,
        cityToRestore: any = {}
    ): GetFilterResponse {
        // Price formatting
        const selectedMin = filters.sections.price_range.selected.min;
        const selectedMax = filters.sections.price_range.selected.max;
        const isUncapped = selectedMax == undefined || String(filters.sections.price_range.selected.exceeds_max_cap) === 'true';

        if (filters.sections.price_range.min_value === filters.sections.price_range.max_value) {
            filters.sections.price_range.max_value += filters.sections.price_range.step_size;
        }
        if (
            selectedMin == undefined ||
            selectedMin < filters.sections.price_range.min_value ||
            (selectedMin >= selectedMax && !isUncapped)
        ) {
            filters.sections.price_range.selected.min = filters.sections.price_range.min_value;
        }
        if (
            isUncapped ||
            selectedMax > filters.sections.price_range.max_value ||
            (selectedMax <= selectedMin && !isUncapped)
        ) {
            filters.sections.price_range.selected.max = filters.sections.price_range.max_value;
            if (isUncapped) {
                filters.sections.price_range.selected.exceeds_max_cap = true;
            }
        }

        // Sorting
        filters.sections.sorting.options.forEach((sortItem) => {
            if (sortItem.key === filters.sections.sorting.selected) {
                sortItem.isSelected = true;
                return;
            }
        });

        // Review/Rating
        filters.sections.rating.options.forEach((reviewItem) => {
            if (reviewItem.key === filters.sections.rating.selected) {
                reviewItem.isSelected = true;
                return;
            }
        });

        // Category
        filters.sections.categories.options.forEach((categoryItem) => {
            categoryItem.color = categoryItem.color ?? '#0081E9'; // TODO Override until working in API
            if (filters.sections.categories.selected.includes(categoryItem.key)) {
                categoryItem.isSelected = true;
            }
        });
        filters.sections.categories.selected = (filters.sections.categories.selected as string).split(',');

        // Distance
        if (
            isNearby &&
            filters.sections.sorting.selected !== 'default' &&
            filters.sections.distance.options[0]?.key === 'all'
        ) {
            filters.sections.distance.options.splice(0, 1);
            if (filters.sections.distance.selected === 'all') {
                filters.sections.distance.selected = '15';
            }
        }
        if (
            Object.keys(cityToRestore).length > 0 &&
            filters.sections.distance.selected !== 'all'
        ) {
            filters.sections.distance.label = cityToRestore.label;
            filters.sections.distance.latitude = cityToRestore.lat;
            filters.sections.distance.longitude = cityToRestore.lng;
        }

        return filters;
    }
}
